/**
 * Zenith motion layer: fades sections and grid items up as they scroll into view, and
 * turns product lists with "Auto-scroll products" on into a slow, endless strip.
 *
 * Runs only when snippets/zenith-animations.liquid has put `zenith-motion` on <html>
 * (never in the theme editor or for reduced-motion visitors). The first section on a
 * page is left alone because it is already on screen. Items re-rendered later, for
 * example by collection filters, arrive without the reveal class and stay visible.
 */
(function () {
  const root = document.documentElement;
  if (!root.classList.contains('zenith-motion')) return;

  if (!('IntersectionObserver' in window)) {
    root.classList.remove('zenith-motion');
    return;
  }

  const ITEM_SELECTOR = '.resource-list__item, .product-grid__item';
  const STAGGER_MS = 80;
  const STAGGER_STEPS = 4;
  const MARQUEE_SECONDS_PER_ITEM = 6;
  const MARQUEE_MAX_FILL_PASSES = 6;

  const FOCUSABLE_SELECTOR = 'a[href], button, input, select, textarea, summary, [tabindex]';
  let cloneCount = 0;

  /**
   * Copies a set of cards for the marquee loop. Copies are hidden from assistive
   * technology and taken out of the tab order, and get suffixed ids so none are duplicated.
   * They are not `inert` and keep an id on every element that had one: product-card.js
   * only navigates from an image click when the card link has an id, so shoppers can
   * click or tap whichever card scrolls past, copy or not.
   * @param {Element[]} items
   * @returns {Element[]}
   */
  function cloneForMarquee(items) {
    return items.map((item) => {
      const clone = /** @type {HTMLElement} */ (item.cloneNode(true));
      const suffix = `-zm${++cloneCount}`;
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll(FOCUSABLE_SELECTOR).forEach((element) => element.setAttribute('tabindex', '-1'));
      [clone, ...clone.querySelectorAll('[id]')].forEach((element) => {
        if (element.id) element.id += suffix;
      });
      return clone;
    });
  }

  /**
   * Turns a product grid into a slow, endless sideways strip. The cards are moved into a
   * track, topped up with copies until one set spans the strip, then the whole set is
   * repeated once so translating the track by -50% loops without a visible jump.
   * @param {HTMLElement} list - The `.resource-list` grid inside a `[data-zenith-marquee]` section.
   */
  function buildMarquee(list) {
    const items = Array.from(list.querySelectorAll(':scope > .resource-list__item'));
    if (items.length < 2) return;

    const track = document.createElement('div');
    track.className = 'zenith-marquee__track';
    track.append(...items);
    list.append(track);
    list.classList.add('zenith-marquee');

    for (let pass = 0; pass < MARQUEE_MAX_FILL_PASSES && track.scrollWidth < list.clientWidth; pass++) {
      track.append(...cloneForMarquee(items));
    }

    const baseSet = Array.from(track.children);
    track.append(...cloneForMarquee(baseSet));
    track.style.setProperty('--zenith-marquee-duration', `${baseSet.length * MARQUEE_SECONDS_PER_ITEM}s`);
  }

  document.querySelectorAll('[data-zenith-marquee] .resource-list').forEach((list) => {
    if (list instanceof HTMLElement) buildMarquee(list);
  });

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0 }
  );

  /**
   * @param {HTMLElement} element
   * @param {number} delay - Stagger delay in milliseconds.
   */
  function prepare(element, delay) {
    if (element.classList.contains('zenith-reveal')) return;
    element.style.setProperty('--zenith-delay', `${delay}ms`);
    element.classList.add('zenith-reveal');
    observer.observe(element);
  }

  const sections = document.querySelectorAll('#MainContent > .shopify-section');

  sections.forEach((section, index) => {
    if (index === 0 || !(section instanceof HTMLElement)) return;

    if (section.querySelector('.zenith-marquee')) {
      prepare(section, 0);
      return;
    }

    const items = section.querySelectorAll(ITEM_SELECTOR);

    if (items.length > 0) {
      items.forEach((item, itemIndex) => {
        if (item instanceof HTMLElement) prepare(item, (itemIndex % STAGGER_STEPS) * STAGGER_MS);
      });
    } else {
      prepare(section, 0);
    }
  });
})();
